import { useState, useEffect, useCallback } from 'react';
import { floorFixturesService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { defaultFloorFixtures } from '../data/floorLayout';
import type { FloorFixture } from '../types';

interface UseFloorFixturesReturn {
  fixtures: FloorFixture[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (fixture: Omit<FloorFixture, 'id'>) => Promise<FloorFixture>;
  update: (id: string, updates: Partial<FloorFixture>) => Promise<FloorFixture>;
  remove: (id: string) => Promise<void>;
}

// Ensure numeric fields from Firestore are numbers (can be stored/returned as strings or missing)
function normalizeFixture(raw: Record<string, unknown>): FloorFixture {
  const num = (v: unknown, fallback: number) =>
    typeof v === 'number' && isFinite(v) ? v : (typeof v === 'string' ? parseFloat(v) : fallback);
  return {
    id: String(raw.id ?? ''),
    label: String(raw.label ?? ''),
    type: (raw.type as FloorFixture['type']) ?? 'custom',
    x: num(raw.x, 50),
    y: num(raw.y, 50),
    width: num(raw.width, 15),
    height: num(raw.height, 15),
    rotation: num(raw.rotation, 0),
  };
}

export const useFloorFixtures = (enabled = true): UseFloorFixturesReturn => {
  const [fixtures, setFixtures] = useState<FloorFixture[]>(defaultFloorFixtures);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFixtures = useCallback(async () => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      setFixtures(defaultFloorFixtures);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await floorFixturesService.getAll();
      // Do not auto-seed defaults when collection is empty.
      // Empty can be a valid user-managed state after deleting/reworking fixtures.
      setFixtures(data.map((f) => normalizeFixture(f as unknown as Record<string, unknown>)));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load floor fixtures';
      setError(errorMessage);
      console.error('Error fetching floor fixtures:', err);
      // Do NOT reset to defaults on error - keep existing layout so positions/settings are not lost
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchFixtures();
  }, [fetchFixtures, enabled]);

  const create = useCallback(async (fixture: Omit<FloorFixture, 'id'>) => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      const id = `${fixture.type}-${Date.now()}`;
      const newFixture: FloorFixture = { ...fixture, id };
      setFixtures(prev => [...prev, newFixture]);
      return newFixture;
    }

    const newFixture = await floorFixturesService.create(fixture);
    const normalized = normalizeFixture(newFixture as unknown as Record<string, unknown>);
    setFixtures(prev => [...prev, normalized]);
    return normalized;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<FloorFixture>) => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setFixtures(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
      return { ...(fixtures.find(f => f.id === id) as FloorFixture), ...updates };
    }

    const updated = await floorFixturesService.update(id, updates);
    const normalized = normalizeFixture(updated as unknown as Record<string, unknown>);
    setFixtures(prev => prev.map(f => f.id === id ? normalized : f));
    return normalized;
  }, [fixtures]);

  const remove = useCallback(async (id: string) => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setFixtures(prev => prev.filter(f => f.id !== id));
      return;
    }

    await floorFixturesService.delete(id);
    setFixtures(prev => prev.filter(f => f.id !== id));
  }, []);

  return {
    fixtures,
    loading,
    error,
    refresh: fetchFixtures,
    create,
    update,
    remove,
  };
};

